import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Icon, Loader, Modal, Text, useTheme } from '@Sage/hoi-poi-ui';
import { getMarketingStatusHistory } from 'services/MarketingStatusService';
import { formatDate } from 'utils/dates';
import { getLiteral } from 'utils/getLiteral';
import MarketingStatusHistorySourceValue from './MarketingStatusHistorySourceValue';
import MarketingStatusReadOnlyChip from './MarketingStatusReadOnlyChip';
import {
    buildMarketingStatusChipOptions,
    fetchMarketingStatusList,
    resolveHistoryStatusOption,
    resolveMarketingStatusHistoryLabel,
} from './helpers';

import './MarketingStatusHistoryModal.scss';

/** @typedef {import('services/MarketingStatusService').MarketingStatusHistoryEntry} MarketingStatusHistoryEntry */

const MARKETING_STATUS_HISTORY_MODAL_CLOSE_TIMEOUT = 300;

function formatHistoryTimestamp(date) {
    return `${formatDate(date, 'L')} · ${formatDate(date, 'LT')}`;
}

function resolveChangedBy(entry) {
    if (entry.ChangedByUserName) {
        return entry.ChangedByUserName;
    }
    return resolveMarketingStatusHistoryLabel(entry.ChangedByLabel);
}

function HistoryTimelineMetaRow({ label, value }) {
    const theme = useTheme();
    const hasValue = value != null && value !== '';

    return (
        <div className="fm-marketing-status-history-modal__timeline-meta-row">
            <Text
                className="fm-marketing-status-history-modal__timeline-meta-row-label"
                type="caption"
                color={theme.colors.utility.textSecondary}
            >
                {label}:
            </Text>
            {hasValue ? (
                typeof value === 'string' ? (
                    <Text type="caption" color={theme.colors.utility.textPrimary}>
                        {value}
                    </Text>
                ) : (
                    value
                )
            ) : null}
        </div>
    );
}

function HistorySummaryMetric({ labelKey, children }) {
    const theme = useTheme();

    return (
        <div className="fm-marketing-status-history-modal__summary-metric">
            <Text
                className="fm-marketing-status-history-modal__summary-metric-label"
                type="caption"
                color={theme.colors.utility.textSecondary}
            >
                {getLiteral(labelKey)}
            </Text>
            <div className="fm-marketing-status-history-modal__summary-metric-value">
                {children}
            </div>
        </div>
    );
}

function HistorySummary({ entries, statusOptions, historyCount }) {
    const theme = useTheme();
    const currentEntry = entries[0];
    const firstEntry = entries[entries.length - 1];
    const currentStatusOption = useMemo(
        () =>
            currentEntry
                ? resolveHistoryStatusOption(currentEntry, statusOptions, theme)
                : undefined,
        [currentEntry, statusOptions, theme],
    );
    const currentStatusColor = currentStatusOption?.textColor ?? theme.colors.actionMinor[600];

    return (
        <div className="fm-marketing-status-history-modal__summary">
            <HistorySummaryMetric labelKey="label_marketing_status_current_status">
                <span
                    className="fm-marketing-status-history-modal__current-status"
                    style={{ color: currentStatusColor }}
                >
                    <span
                        className="fm-marketing-status-history-modal__current-status-dot"
                        style={{ backgroundColor: currentStatusColor }}
                    />
                    <Text type="caption" color={currentStatusColor}>
                        {currentStatusOption?.label ?? ''}
                    </Text>
                </span>
            </HistorySummaryMetric>
            <HistorySummaryMetric labelKey="label_marketing_status_total_changes">
                <Text type="subtitle" medium>
                    {historyCount}
                </Text>
            </HistorySummaryMetric>
            <HistorySummaryMetric labelKey="label_marketing_status_from">
                <Text type="subtitle" medium>
                    {formatDate(firstEntry?.ChangeDate, 'L')}
                </Text>
            </HistorySummaryMetric>
        </div>
    );
}

function HistoryTimelineEntry({ entry, statusOption, onNavigate }) {
    const theme = useTheme();
    const dotColor = statusOption?.textColor ?? theme.colors.actionMinor[600];
    const changedBy = resolveChangedBy(entry);

    return (
        <div className="fm-marketing-status-history-modal__timeline-entry">
            <div className="fm-marketing-status-history-modal__timeline-marker">
                <span
                    className="fm-marketing-status-history-modal__timeline-dot"
                    style={{ borderColor: dotColor }}
                >
                    <span
                        className="fm-marketing-status-history-modal__timeline-dot-inner"
                        style={{ backgroundColor: dotColor }}
                    />
                </span>
            </div>
            <div className="fm-marketing-status-history-modal__timeline-content">
                <div className="fm-marketing-status-history-modal__timeline-header">
                    <MarketingStatusReadOnlyChip option={statusOption} />
                    <Text type="caption" color={theme.colors.utility.textSecondary}>
                        {formatHistoryTimestamp(entry.ChangeDate)}
                    </Text>
                </div>
                <div className="fm-marketing-status-history-modal__timeline-meta">
                    <HistoryTimelineMetaRow
                        label={getLiteral('label_marketing_status_by')}
                        value={changedBy}
                    />
                    <HistoryTimelineMetaRow
                        label={getLiteral('label_marketing_status_source')}
                        value={
                            <MarketingStatusHistorySourceValue
                                sourceLabel={entry.SourceLabel}
                                onNavigate={onNavigate}
                            />
                        }
                    />
                </div>
            </div>
        </div>
    );
}

function HistoryTimeline({ entries, statusOptions, theme, onNavigate }) {
    return (
        <div className="fm-marketing-status-history-modal__timeline">
            {entries.map((entry) => (
                <HistoryTimelineEntry
                    key={entry.Id}
                    entry={entry}
                    statusOption={resolveHistoryStatusOption(entry, statusOptions, theme)}
                    onNavigate={onNavigate}
                />
            ))}
        </div>
    );
}

const MarketingStatusHistoryModal = memo(
    ({ isOpen, onClose, entityType, entityId, historyCount }) => {
        const dispatch = useDispatch();
        const theme = useTheme();
        const [entries, setEntries] = useState([]);
        const [listRows, setListRows] = useState([]);
        const [isLoading, setIsLoading] = useState(false);
        const [hasError, setHasError] = useState(false);
        const closeCleanupTimeoutRef = useRef(null);

        const statusOptions = useMemo(
            () => buildMarketingStatusChipOptions(listRows, theme),
            [listRows, theme],
        );

        const modalTitle = useMemo(
            () => (
                <div className="fm-marketing-status-history-modal__header">
                    <Text type="body" bold>
                        {getLiteral('label_marketing_status_history_title')}
                    </Text>
                    <Text
                        type="caption"
                        color={theme.colors.utility.textSecondary}
                        className="fm-marketing-status-history-modal__header-subtitle"
                    >
                        <Icon name="infoOutlined" color="currentColor" />
                        {getLiteral('label_marketing_status_history_desc')}
                    </Text>
                </div>
            ),
            [theme.colors.utility.textSecondary],
        );

        useEffect(() => {
            if (!isOpen) {
                return undefined;
            }

            let cancelled = false;

            fetchMarketingStatusList(dispatch)
                .then((rows) => {
                    if (!cancelled) {
                        setListRows(Array.isArray(rows) ? rows : []);
                    }
                })
                .catch(() => {
                    if (!cancelled) {
                        setListRows([]);
                    }
                });

            return () => {
                cancelled = true;
            };
        }, [dispatch, isOpen]);

        useEffect(() => {
            clearTimeout(closeCleanupTimeoutRef.current);

            if (!isOpen) {
                closeCleanupTimeoutRef.current = setTimeout(() => {
                    setEntries([]);
                    setHasError(false);
                    setIsLoading(false);
                    closeCleanupTimeoutRef.current = null;
                }, MARKETING_STATUS_HISTORY_MODAL_CLOSE_TIMEOUT);

                return () => {
                    clearTimeout(closeCleanupTimeoutRef.current);
                };
            }

            if (!entityType || !Number.isFinite(entityId) || entityId <= 0) {
                setEntries([]);
                setHasError(false);
                setIsLoading(false);
                return undefined;
            }

            let cancelled = false;
            setIsLoading(true);
            setHasError(false);
            setEntries([]);

            getMarketingStatusHistory({ entityType, entityId })
                .then((result) => {
                    if (!cancelled) {
                        setEntries(Array.isArray(result) ? result : []);
                    }
                })
                .catch(() => {
                    if (!cancelled) {
                        setHasError(true);
                        setEntries([]);
                    }
                })
                .finally(() => {
                    if (!cancelled) {
                        setIsLoading(false);
                    }
                });

            return () => {
                cancelled = true;
            };
        }, [entityId, entityType, isOpen]);

        const hasEntries = entries.length > 0;

        return (
            <Modal
                isOpen={isOpen}
                onRequestClose={onClose}
                title={modalTitle}
                size="medium"
                closeTimeoutMS={MARKETING_STATUS_HISTORY_MODAL_CLOSE_TIMEOUT}
            >
                {isLoading ? (
                    <div className="fm-marketing-status-history-modal__loader">
                        <Loader />
                    </div>
                ) : null}
                {!isLoading && hasError ? (
                    <Text>{getLiteral('label_marketing_status_history_load_error')}</Text>
                ) : null}
                {!isLoading && !hasError && hasEntries ? (
                    <div className="fm-marketing-status-history-modal__panel">
                        <HistorySummary
                            entries={entries}
                            statusOptions={statusOptions}
                            historyCount={historyCount}
                        />
                        <HistoryTimeline
                            entries={entries}
                            statusOptions={statusOptions}
                            theme={theme}
                            onNavigate={onClose}
                        />
                    </div>
                ) : null}
            </Modal>
        );
    },
);

MarketingStatusHistoryModal.displayName = 'MarketingStatusHistoryModal';

export default MarketingStatusHistoryModal;
